import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import type { DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, patch, post, getTodos } from '../../nucleo/api';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { CamposForm, GRADE_FORM, useEstadoForm } from './form';
import { Historico } from './Historico';
import type { Modelo } from './tipos';

const VARIAVEIS = ['nome', 'valor', 'vencimento', 'link', 'pix', 'linha_digitavel', 'pix_copia_e_cola', 'link_portal', 'como_pagar'] as const;

function useAtrasado<T>(v: T, ms = 350) {
  const [d, setD] = useState(v);
  useEffect(() => {
    const id = setTimeout(() => setD(v), ms);
    return () => clearTimeout(id);
  }, [v, ms]);
  return d;
}

interface Previa {
  assunto: string;
  corpo: string;
  canal: 'email' | 'whatsapp' | null;
}

function PreviaModelo({ assunto, corpo, comAssunto }: { assunto: string; corpo: string; comAssunto: boolean }) {
  const { t } = useTranslation('configuracoes');
  const chave = useAtrasado(JSON.stringify([assunto, corpo]));
  const q = useQuery({
    queryKey: ['configuracoes', 'modelos', 'previa', chave],
    queryFn: () => post<Previa>('/configuracoes/modelos/previa', { assunto: comAssunto ? assunto : '', corpo }),
    enabled: corpo.trim().length > 0,
    placeholderData: (anterior) => anterior,
  });
  return (
    <section aria-label={t('modelos.previa')} aria-live="polite" className="flex min-w-0 flex-col gap-2 rounded-xl border border-borda bg-sup-2 p-4 sm:col-span-6">
      <p className="kicker">{t('modelos.previa')}</p>
      {q.isError ? (
        <p className="text-sm text-perigo">{mensagemDeErro(q.error)}</p>
      ) : !q.data ? (
        <p className="text-sm text-tinta-3">{corpo.trim() ? t('modelos.previaCarregando') : ''}</p>
      ) : (
        <>
          {comAssunto && q.data.assunto && <p className="text-sm font-medium break-words">{q.data.assunto}</p>}
          <p className="text-sm break-words whitespace-pre-wrap text-tinta-2">{q.data.corpo}</p>
        </>
      )}
    </section>
  );
}

function FormModelo({ modelo, aoSalvar }: { modelo: Modelo | null; aoSalvar: () => Promise<unknown> }) {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const editando = !!modelo;
  const bloqueado = !podeNoWorkspace(s, 'configuracoes.editar');
  const f = useEstadoForm<Record<string, unknown>>({
    codigo: modelo?.codigo ?? '',
    nome: modelo?.nome ?? '',
    tipo: modelo?.tipo ?? 'mensagem',
    canal: modelo?.canal ?? 'email',
    assunto: modelo?.assunto ?? '',
    corpo: modelo?.corpo ?? '',
    ativo: modelo?.ativo ?? true,
  });
  const defs: DefCampo[] = [
    { nome: 'nome', rotulo: t('modelos.nome'), tipo: 'texto', obrigatorio: true, desabilitado: bloqueado },
    { nome: 'codigo', rotulo: t('modelos.codigo'), tipo: 'texto', obrigatorio: true, largura: 'meia', ajuda: t('modelos.codigoDica'), desabilitado: editando || bloqueado },
    { nome: 'ativo', rotulo: t('modelos.ativo'), tipo: 'booleano', largura: 'meia', desabilitado: bloqueado },
    { nome: 'tipo', rotulo: t('modelos.tipo'), tipo: 'selecao', obrigatorio: true, largura: 'meia', desabilitado: bloqueado, opcoes: (['mensagem', 'documento'] as const).map((x) => ({ valor: x, rotulo: t(`modelos.tipos.${x}`) })) },
    { nome: 'canal', rotulo: t('modelos.canal'), tipo: 'selecao', obrigatorio: true, largura: 'meia', desabilitado: bloqueado, opcoes: (['email', 'whatsapp'] as const).map((x) => ({ valor: x, rotulo: t(`modelos.canais.${x}`) })) },
    { nome: 'assunto', rotulo: t('modelos.assunto'), tipo: 'texto', obrigatorio: true, visivel: (v) => v.canal === 'email', desabilitado: bloqueado },
    { nome: 'corpo', rotulo: t('modelos.corpo'), tipo: 'area', obrigatorio: true, desabilitado: bloqueado },
  ];
  const comAssunto = f.valores.canal === 'email';

  return (
    <form
      id="form-modelo"
      noValidate
      className={GRADE_FORM}
      onSubmit={(ev) =>
        f.enviar(ev, defs, tn('comum.obrigatorio'), async (v) => {
          const comum = { nome: v.nome, tipo: v.tipo, canal: v.canal, assunto: v.canal === 'email' ? v.assunto : '', corpo: v.corpo, ativo: !!v.ativo };
          if (modelo) await patch(`/configuracoes/modelos/${modelo.id}`, comum);
          else await post('/configuracoes/modelos', { ...comum, codigo: String(v.codigo ?? '').trim() });
          await aoSalvar();
        })
      }
    >
      <CamposForm defs={defs} valores={f.valores} erros={f.erros} erroGeral={f.erroGeral} aoMudar={f.definir}>
        {!bloqueado && (
          <div className="flex min-w-0 flex-col gap-2 sm:col-span-6">
            <p className="text-sm font-medium text-tinta-2">
              {t('modelos.variaveis')} <span className="text-xs font-normal text-tinta-3">({t('modelos.variaveisDica')})</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {VARIAVEIS.map((x) => (
                <button
                  key={x}
                  type="button"
                  title={t(`modelos.var.${x}`)}
                  onClick={() => f.definir('corpo', `${String(f.valores.corpo ?? '')}{{${x}}}`)}
                  className="numero rounded-full border border-borda px-3 py-1 text-xs text-tinta-2 hover:border-acento-forte hover:text-tinta-1"
                >
                  {`{{${x}}}`}
                </button>
              ))}
            </div>
          </div>
        )}
      </CamposForm>
      <PreviaModelo assunto={String(f.valores.assunto ?? '')} corpo={String(f.valores.corpo ?? '')} comAssunto={comAssunto} />
    </form>
  );
}

/** M13.4: modelos de mensagem e documento com prévia renderizada. */
export function Modelos() {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const podeEditar = podeNoWorkspace(s, 'configuracoes.editar');
  const [aberto, setAberto] = useState<string | 'novo' | null>(null);
  const [arquivando, setArquivando] = useState<Modelo | null>(null);
  const q = useQuery({ queryKey: ['configuracoes', 'modelos'], queryFn: () => getTodos<Modelo>('/configuracoes/modelos') });
  const atual = aberto && aberto !== 'novo' ? (q.data?.itens.find((m) => m.id === aberto) ?? null) : null;

  const recarregar = () => Promise.all([qc.invalidateQueries({ queryKey: ['configuracoes', 'modelos'] }), qc.invalidateQueries({ queryKey: ['configuracoes', 'historico'] })]);
  const arquivar = useMutation({
    mutationFn: (id: string) => del(`/configuracoes/modelos/${id}`),
    onSuccess: async () => {
      setArquivando(null);
      await recarregar();
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => {
      setArquivando(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {podeEditar && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
            {t('acoes.novoModelo')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Cartao titulo={t('modelos.titulo')} semPadding>
        <p className="border-b border-borda px-4 py-3 text-sm text-tinta-3">{t('modelos.ajuda')}</p>
        <Estado consulta={q}>
          {(d) =>
            d.itens.length === 0 ? (
              <Vazio texto={t('modelos.vazio')} />
            ) : (
              <ul className="divide-y divide-borda">
                {d.itens.map((m) => (
                  <li key={m.id} className="flex min-w-0 items-center gap-2 pr-3 hover:bg-sup-2">
                    <button type="button" onClick={() => setAberto(m.id)} className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left">
                      <span className="flex min-w-0 flex-1 basis-44 flex-col">
                        <span className="truncate font-medium" title={m.nome}>{m.nome}</span>
                        <span className="numero truncate text-xs text-tinta-3" title={m.codigo}>{m.codigo}</span>
                      </span>
                      <Selo cor={m.canal === 'whatsapp' ? 'verde' : 'azul'}>{t(`modelos.canais.${m.canal}`)}</Selo>
                      <Selo>{t(`modelos.tipos.${m.tipo}`)}</Selo>
                      {!m.ativo && <Selo cor="laranja">{t('modelos.inativo')}</Selo>}
                    </button>
                    {podeEditar && (
                      <button type="button" aria-label={`${t('acoes.arquivar')}: ${m.nome}`} onClick={() => setArquivando(m)} className="rounded p-1.5 text-tinta-3 hover:text-perigo">
                        <Trash2 className="size-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )
          }
        </Estado>
      </Cartao>

      <Historico entidade="configuracoes.modelo" titulo={t('modelos.historico')} />

      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        largura="lg"
        titulo={atual ? t('modelos.editarTitulo') : t('modelos.novoTitulo')}
        rodape={
          podeEditar && (
            <>
              <Botao variante="fantasma" onClick={() => setAberto(null)}>
                {tn('comum.cancelar')}
              </Botao>
              <Botao variante="primario" type="submit" form="form-modelo">
                {tn('comum.salvar')}
              </Botao>
            </>
          )
        }
      >
        <FormModelo
          key={atual?.id ?? 'novo'}
          modelo={atual}
          aoSalvar={async () => {
            await recarregar();
            toast.sucesso(tn('comum.salvo'));
            setAberto(null);
          }}
        />
      </Drawer>

      <Confirmar
        aberto={!!arquivando}
        aoFechar={() => setArquivando(null)}
        aoConfirmar={() => arquivando && arquivar.mutate(arquivando.id)}
        carregando={arquivar.isPending}
        perigoso
        titulo={t('modelos.confirmarArquivar')}
        texto={t('modelos.confirmarArquivarTexto')}
      />
    </div>
  );
}
